import { Box } from "@chakra-ui/react";
import { POSTS_STATE_VALUE_PUBLISHED } from "@raiden/library/constants/posts";
import { useSearchInfiniteContext } from "@raiden/library/contexts/SearchInfinite/useSearchInfiniteContext";
import { apiGetErrorDetail } from "@raiden/library/helpers/api";
import useRequest from "@raiden/library/hooks/useRequest";
import useTranslate from "@raiden/library/hooks/useTranslate";
import downloadBlob from "@raiden/library/libraries/utils/downloadBlob";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import { useIntl } from "react-intl";
import { Href } from "../../../../../../components/Href";
import { SEARCH_RESULTS_OBJECT_TYPE_FILE } from "../../../../../../constants/search/results";
import { getPostPath } from "../../../../../../helpers/posts/getPostPath";
import { SearchResultContent } from "./Content";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/search/Result").SearchResult} searchResult
 * @property {() => void} onClose
 */
/**
 * @param {Props} props
 */
export function SearchResultTemplate(props) {
  const { searchResult, onClose } = props;

  const intl = useIntl();

  const translate = useTranslate();

  /** @type {import("@raiden/library/contexts/SearchInfinite/useSearchInfiniteContext").UseSearchInfiniteReturn<import(".").SearchResultsSearchFormValues, import("@raiden/library/types/search/Result").SearchResult[]>} */
  const {
    form: { getValues },
  } = useSearchInfiniteContext();

  const termValue = getValues("term");

  const { request, toastSuccess } = useRequest();

  function handleClickDownload({ searchResult }) {
    request(
      generateApiUrl({
        id: "@api.files.download",
        parameters: {
          fileId: searchResult?.object_id,
        },
      }),
      {
        method: "GET",
      },
      {
        outputMode: "file",
      },
    )
      .then(function (response) {
        if (response?.data?.contentType === "blob") {
          downloadBlob({
            blob: response?.data?.blob,
            filename:
              translate(searchResult.translations.title) ?? "export.csv",
          });
          toastSuccess({
            title: intl.formatMessage({
              defaultMessage: "Le fichier a été téléchargé.",
            }),
          });
        }
      })
      .catch((response) => {
        toastSuccess({
          title: response?.body?.errors?.[0]?.detail
            ? apiGetErrorDetail({ error: response })
            : intl.formatMessage({
                defaultMessage:
                  "Une erreur est survenue lors du téléchargement du fichier.",
              }),
          status: "error",
        });
      });
  }

  return (
    <>
      {(() => {
        switch (searchResult.object_type) {
          case SEARCH_RESULTS_OBJECT_TYPE_FILE:
            return (
              <Box
                cursor="pointer"
                onClick={() => handleClickDownload({ searchResult })}>
                <SearchResultContent
                  key={searchResult.id}
                  searchResult={searchResult}
                  termValue={termValue}
                />
              </Box>
            );
          default:
            if (
              searchResult.object_id &&
              searchResult.metadata.state === POSTS_STATE_VALUE_PUBLISHED
            ) {
              return (
                <Href
                  href={getPostPath({
                    postId: searchResult.object_id,
                    postType: searchResult.metadata.type,
                    postSlug: searchResult.metadata.slug,
                    translate,
                  })}
                  onClick={onClose}>
                  <SearchResultContent
                    key={searchResult.id}
                    searchResult={searchResult}
                    termValue={termValue}
                  />
                </Href>
              );
            } else {
              if (translate(searchResult.translations?.url)) {
                return (
                  <Href
                    href={translate(searchResult.translations?.url)}
                    onClick={onClose}>
                    <SearchResultContent
                      key={searchResult.id}
                      searchResult={searchResult}
                      termValue={termValue}
                    />
                  </Href>
                );
              }

              return (
                <SearchResultContent
                  key={searchResult.id}
                  searchResult={searchResult}
                  termValue={termValue}
                />
              );
            }
        }
      })()}
    </>
  );
}
